import React, {ChangeEvent, useState} from 'react';
import Card from 'components/card';

import {createColumnHelper, flexRender, getCoreRowModel, getFilteredRowModel, useReactTable} from '@tanstack/react-table';
import {PSPType, WalletTransactionItem} from '../../../../stores/types';
import {formatCurrency, longDateFormatStr} from '../../../../stores/JSHelper';
import {MdDownload, MdRefresh} from 'react-icons/md';
import {FiSearch} from 'react-icons/fi';
import UseAccountStore from '../../../../stores/DataStore';
import {isCentralAccount} from '../../../../stores/Constants';
import DateRangePicker from 'components/DateRangePicker/DateRangePicker';
import PspFilter from 'components/widget/PspFilter';
import Psp from 'components/widget/Psp';

const escapeRegexCharacters = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

function LedgerTransactionsTable(props: {
  transactions: WalletTransactionItem[];
  refresh: (force?: boolean) => void;
  downloadAccountStatement: () => Promise<any>;
  psp: PSPType,
  setPsp: (psp: PSPType) => Promise<void>;
}) {
  const {transactions, refresh, downloadAccountStatement, psp, setPsp} = props;
  const [globalFilter, setGlobalFilter] = useState('');
  const state = UseAccountStore();
  const [downloadLoading, setDownloadLoading] = useState(false);

  const setReportRange = (daysSince: string, startDate?: Date, endDate?: Date) => {
    state.setDateRange(daysSince, startDate, endDate);
    state.refresh();
  };

  const refreshPage = () => {
    refresh();
    state.refresh(true);
  };

  const onChangePsp = async (psp: PSPType) => {
    await setPsp(psp);
    refresh();
  }

  const columns = [
    columnHelper.accessor('transactionDate', {
      id: 'date',
      header: () => <p className="text-sm font-bold text-gray-600 dark:text-white">Date</p>,
      cell: (info: any) => (
        <div className="flex items-center">
          <p className="ml-3 text-sm font-bold text-navy-700 dark:text-white">{longDateFormatStr(info.getValue())}</p>
        </div>
      ),
    }),
    columnHelper.accessor((row) => row, {
      id: 'partyName',
      header: () => <p className="text-sm font-bold text-gray-600 dark:text-white">Party</p>,
      cell: (info) => (
        <p className="text-sm font-bold text-navy-700 dark:text-white">
          <div>
            <a href="#" className="hover:bg-indigo-100" onClick={() => state.setAccount(info.getValue().accountId)}>
              {info.getValue().accountName}
            </a>
          </div>
          <div>
            <small>
              {isCentralAccount(state.account.accountId) && <span>{info.getValue().requesterAccountName} | </span>}
              {info.getValue().partyId} | {info.getValue().updatedBy}
            </small>
          </div>
        </p>
      ),
    }),
    columnHelper.accessor('utr', {
      id: 'UTR',
      header: () => <p className="text-center text-sm font-bold text-gray-600 dark:text-white">UTR</p>,
      cell: (info) => <p className="text-center text-sm font-bold text-navy-700 dark:text-white">
        <div className='flex flex-row items-center justify-center'>
          <div className='mr-2' >
            <Psp empty pspType={info.row.original.pspType} />
          </div>
          {info.getValue()}
        </div>
      </p>,
    }),
    columnHelper.accessor('credit', {
      id: 'credit',
      header: () => <p className="text-right text-sm font-bold text-gray-600 dark:text-white">Credit</p>,
      cell: (info) => (
        <p className="text-right text-sm font-bold text-navy-700 dark:text-white">
          {info.getValue() == 0 ? '-' : formatCurrency(info.getValue(), 'INR', false)}
        </p>
      ),
    }),
    columnHelper.accessor('debit', {
      id: 'debit',
      header: () => <p className="text-right text-sm font-bold text-gray-600 dark:text-white">Debit</p>,
      cell: (info) => (
        <p className="text-right text-sm font-bold text-navy-700 dark:text-white">
          {info.getValue() == 0 ? '-' : formatCurrency(info.getValue(), 'INR', false)}
        </p>
      ),
    }),
    columnHelper.accessor('closingBalance', {
      id: 'closingBalance',
      header: () => <p className="text-right text-sm font-bold text-gray-600 dark:text-white">Balance</p>,
      cell: (info) => <p className="text-right text-sm font-bold text-navy-700 dark:text-white">{formatCurrency(info.getValue(), 'INR', false)}</p>,
    }),
  ]; // eslint-disable-next-line

  const table = useReactTable({
    data: transactions,
    columns,
    enableSorting: false,
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: (row, columnId, filterValue) => {
      const safeValue = (() => {
        const value = row.getValue(columnId);
        return typeof value === 'number' ? String(value) : value;
      })();

      return `${safeValue}`?.toLowerCase().includes(filterValue.toLowerCase());
    },
    onSortingChange: () => false,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const globalFilterHandler = (event: ChangeEvent<HTMLInputElement>): void => {
    setGlobalFilter(event.target.value);
  };

  const inactiveDateRangeClasses =
    'rounded-xl bg-navy-700 px-5 py-3 text-sm font-medium text-white transition duration-200 hover:bg-navy-800 active:bg-navy-900 dark:bg-white/10 dark:text-white dark:hover:bg-white/20 dark:active:bg-white/30';
  const activeDateRangeClasses =
    'rounded-xl bg-blue-500 px-5 py-3 text-sm font-medium text-white transition duration-200 hover:bg-blue-600 active:bg-blue-700 dark:bg-blue-400 dark:text-white dark:hover:bg-blue-300 dark:active:bg-blue-200';

  return (
    <Card extra={'w-full h-full sm:overflow-auto'}>
      <header className="relative flex items-center justify-between px-6 pt-4">
        <div className="text-xl font-bold text-navy-700 dark:text-white">Ledger Transactions</div>
        <div className="relative mt-[3px] flex h-[61px] w-[555px] flex-grow items-center justify-around gap-2 rounded-full bg-white px-2 py-2 shadow-xl shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none md:w-[565px] md:flex-grow-0 md:gap-1 xl:w-[565px] xl:gap-2">
          <div className="flex h-full items-center rounded-full bg-lightPrimary text-navy-700 dark:bg-navy-900 dark:text-white xl:w-[525px]">
            <p className="pl-3 pr-2 text-xl">
              <FiSearch className="h-4 w-4 text-gray-400 dark:text-white" />
            </p>
            <input
              value={globalFilter ?? ''}
              onChange={globalFilterHandler}
              type="text"
              placeholder="Search..."
              className="block h-full w-full rounded-full bg-lightPrimary text-sm font-medium text-navy-700 outline-none placeholder:!text-gray-400 dark:bg-navy-900 dark:text-white dark:placeholder:!text-white sm:w-fit"
            />
          </div>
        </div>
      </header>

      <div className="mt-10 flex flex-row justify-end gap-3 px-6">
        {[
          {label: 'Today', value: 'day'},
          {label: 'Week', value: 'week'},
          {label: 'Month', value: 'month'},
          {label: 'Quarter', value: 'quarter'},
          {
            label: 'Year',
            value: 'year',
          },
        ].map((item) => (
          <button
            onClick={async () => setReportRange(item.value)}
            className={state.dateRange === item.value ? activeDateRangeClasses : inactiveDateRangeClasses}
          >
            {item.label}
          </button>
        ))}
        <DateRangePicker active={state.dateRange === "custom"} onChange={(startDate: Date, endDate: Date) => { setReportRange("custom", startDate, endDate); }} />
        <PspFilter psp={psp} onChangePsp={onChangePsp} />
        <button
          onClick={() => refreshPage()}
          className="flex flex-row items-center rounded-xl bg-gray-100 px-4 py-3 text-sm font-medium text-navy-700 transition duration-200 hover:bg-gray-200 active:bg-gray-300 dark:bg-white/10 dark:text-white dark:hover:bg-white/20 dark:active:bg-white/30"
        >
          Refresh <MdRefresh className="text-lg" />
        </button>
        <button
          onClick={async () => {
            setDownloadLoading(true);
            const pdfURL = await downloadAccountStatement();
            // window.open(pdfURL, '_blank', 'noreferrer');
            var link = document.createElement('a');
            link.href = pdfURL;
            const splitUrl = String(pdfURL).split("/");
            link.rel = "noreferrer";
            // link.target = "_blank";
            link.download = splitUrl[splitUrl.length - 1];
            link.dispatchEvent(new MouseEvent('click'));
            link.remove();
            setDownloadLoading(false);
          }}
          className="flex flex-row items-center rounded-xl bg-gray-100 px-4 py-3 text-sm font-medium text-navy-700 transition duration-200 hover:bg-gray-200 active:bg-gray-300 dark:bg-white/10 dark:text-white dark:hover:bg-white/20 dark:active:bg-white/30"
        >
          {
            !downloadLoading ? (
              <>
              Download <MdDownload className="text-lg" />
              </>
            ) : (
              <>
              Downloading...
              </>
            )
          }
        </button>
      </div>

      <div className="mt-8 overflow-x-scroll xl:overflow-x-hidden">
        <table className="w-full">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="!border-px !border-gray-400">
                {headerGroup.headers.map((header) => {
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      onClick={() => false}
                      className="cursor-pointer border-b-[1px] border-gray-200 px-6 pb-2 pr-4 pt-4 text-start"
                    >
                      <div className="items-center justify-between text-xs text-gray-200">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {{
                          asc: '',
                          desc: '',
                        }[header.column.getIsSorted() as string] ?? null}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          {table.getRowModel().rows.length > 0 && (
            <tbody>
              {table.getRowModel().rows.map((row) => {
                return (
                  <tr key={row.id} className="odd:bg-gray-50 [&>*:last-child]:pr-3">
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <td key={cell.id} className="min-w-[150px] border-white/0 py-3">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          )}
          {table.getRowModel().rows.length == 0 && (
            <tbody>
              <tr>
                <td colSpan={100} className="py-5 text-center">
                  No recent transactions
                </td>
              </tr>
            </tbody>
          )}
        </table>
      </div>
    </Card>
  );
}

export default LedgerTransactionsTable;
const columnHelper = createColumnHelper<WalletTransactionItem>();
